/* Product Landing v1 - shared, dependency-free interactivity.
 *
 *   1. initVideoSliders() - center-focus 9:16 video carousel with an infinite
 *      loop built from a tripled slide set. Click-to-play, no autoplay,
 *      no preload: a video costs 0 bytes until its card is active AND clicked.
 *   2. <lv1-carousel> - clamped image/media carousel custom element.
 *   3. initReviewSync() - keeps a quote pane in step with a media carousel.
 *
 * Ported from assets/page-equestrian.js with every `eq-*` hook renamed to
 * `lv1-*`. The two files share no CSS, markup or metaobjects; this one is
 * loaded only by sections/v03-page-sections.liquid.
 *
 * Conventions:
 *   - all JS hooks are `data-lv1-*` attributes, never classes
 *   - the only state classes written are `is-active` and `is-playing`
 *   - every querySelector result is guarded; a missing node degrades to static
 */

(function () {
  if (typeof document === 'undefined') return;

  function debounce(fn, ms) {
    let t;
    return function () { clearTimeout(t); t = setTimeout(fn, ms); };
  }

  // -------------------------------------------------------------------------
  // Video slider - center-focused, click-to-play, infinite loop.
  //
  // To get continuous left + right neighbors at every logical position
  // (including the first and last original slide), we triple the slide set
  // in the DOM (3 copies of the originals) and always operate inside the
  // MIDDLE copy. When the user navigates past the middle copy's bounds, we
  // silently snap the track back to the equivalent position in the middle
  // copy after the animation finishes - the swap is invisible because the
  // destination slide is a clone of the same original.
  //
  // Videos use `preload="none"` so nothing is fetched until the user clicks
  // the play button on the active card. Clones add no network cost.
  //
  // Markup contract:
  //   [data-lv1-vslider][data-count]      root / viewport
  //     [data-lv1-vslider-track]          the translated track
  //       [data-lv1-vslider-slide][data-index]
  //         [data-lv1-vslider-video]      optional <video>
  //         [data-lv1-vslider-play]       optional play overlay button
  //     [data-lv1-vslider-prev] / [data-lv1-vslider-next]
  // -------------------------------------------------------------------------

  function initVideoSliders() {
    document.querySelectorAll('[data-lv1-vslider]').forEach((root) => {
      const track = root.querySelector('[data-lv1-vslider-track]');
      const originals = Array.from(root.querySelectorAll('[data-lv1-vslider-slide]'));
      const N = originals.length;
      if (N === 0 || !track) return;
      const prev = root.querySelector('[data-lv1-vslider-prev]');
      const next = root.querySelector('[data-lv1-vslider-next]');

      // Triple the slides for looping. Skip if only one slide (no loop needed).
      if (N >= 2) {
        for (let pass = 0; pass < 2; pass++) {
          originals.forEach((slide) => {
            const clone = slide.cloneNode(true);
            clone.setAttribute('data-clone', 'true');
            track.appendChild(clone);
          });
        }
      }

      const slides = Array.from(track.querySelectorAll('[data-lv1-vslider-slide]'));
      const totalSlides = slides.length;         // either N or 3N
      const middleStart = N >= 2 ? N : 0;        // first index in the middle copy
      const middleEnd = N >= 2 ? 2 * N : 0;      // first index AFTER the middle copy
      let active = middleStart;                  // DOM index, always inside the middle copy on render
      if (active >= totalSlides) active = 0;

      function pauseAll() {
        slides.forEach((s) => {
          const v = s.querySelector('[data-lv1-vslider-video]');
          if (v && !v.paused) {
            v.pause();
            try { v.currentTime = 0; } catch (e) {}
          }
          s.classList.remove('is-playing');
        });
      }

      function applyTransform() {
        const card = slides[active];
        if (!card) return;
        // Browsers sometimes scroll an overflow-clipped container when a
        // descendant gets focus. Reset so our translateX math anchors to a
        // stable origin every time.
        if (root.scrollLeft !== 0) root.scrollLeft = 0;
        const viewportW = root.getBoundingClientRect().width;
        const cardW = card.offsetWidth;
        const cardCenter = card.offsetLeft + cardW / 2;
        const offset = viewportW / 2 - cardCenter;
        track.style.transform = 'translateX(' + offset + 'px)';
      }

      function render() {
        slides.forEach((s, i) => {
          const isActive = i === active;
          s.setAttribute('aria-current', isActive ? 'true' : 'false');
          const playBtn = s.querySelector('[data-lv1-vslider-play]');
          // Only the active card's play button is reachable by keyboard -
          // the clones and the off-center cards stay out of the tab order.
          if (playBtn) playBtn.tabIndex = isActive ? 0 : -1;
        });
        applyTransform();
      }

      // After an animated move, if we've left the middle copy, silently jump
      // back to the equivalent slot in the middle copy. The user never sees
      // the swap because the slide at the destination is a clone of the same
      // original they were viewing.
      function snapBackIfNeeded() {
        if (N < 2) return;
        if (active >= middleStart && active < middleEnd) return;
        const original = ((active % N) + N) % N;
        const target = middleStart + original;
        const previousTransition = track.style.transition;
        track.style.transition = 'none';
        active = target;
        applyTransform();
        // Force a reflow before restoring the transition so the snap is not animated.
        // eslint-disable-next-line no-unused-expressions
        track.offsetHeight;
        track.style.transition = previousTransition;
        render();
      }

      function go(delta) {
        pauseAll();
        active += delta;
        render();
      }

      function goToDomIndex(i) {
        if (i === active) return;
        pauseAll();
        active = i;
        render();
      }

      function playSlide(slide) {
        const video = slide.querySelector('[data-lv1-vslider-video]');
        if (!video) return;
        pauseAll();
        slide.classList.add('is-playing');
        const p = video.play();
        if (p && typeof p.catch === 'function') {
          p.catch(() => slide.classList.remove('is-playing'));
        }
      }

      slides.forEach((slide, i) => {
        slide.addEventListener('click', (e) => {
          const target = e.target;
          const playBtn = target && typeof target.closest === 'function'
            ? target.closest('[data-lv1-vslider-play]')
            : null;
          if (playBtn) {
            // The play button only fires on the card that is already centered;
            // on a neighbour the click falls through to promoting the card.
            if (i === active) {
              e.preventDefault();
              playSlide(slide);
            }
            return;
          }
          if (i !== active) goToDomIndex(i);
        });
        const video = slide.querySelector('[data-lv1-vslider-video]');
        if (video) {
          video.addEventListener('ended', () => { slide.classList.remove('is-playing'); });
          video.addEventListener('pause', () => { slide.classList.remove('is-playing'); });
        }
      });

      prev && prev.addEventListener('click', () => go(-1));
      next && next.addEventListener('click', () => go(1));

      track.addEventListener('transitionend', (e) => {
        // Only react when the transform itself finishes (avoid filter/opacity).
        if (e.propertyName !== 'transform') return;
        snapBackIfNeeded();
      });

      // Touch swipe - single-finger horizontal swipe changes the active card.
      let touchStartX = null;
      let touchStartY = null;
      track.addEventListener('touchstart', (e) => {
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
      }, { passive: true });
      track.addEventListener('touchend', (e) => {
        if (touchStartX === null) return;
        const dx = e.changedTouches[0].clientX - touchStartX;
        const dy = e.changedTouches[0].clientY - touchStartY;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
          go(dx < 0 ? 1 : -1);
        }
        touchStartX = touchStartY = null;
      }, { passive: true });

      window.addEventListener('resize', debounce(applyTransform, 120));
      // Late-loading media changes card widths, so re-center once everything
      // has settled. rAF keeps the write out of the load handler's frame.
      window.addEventListener('load', () => requestAnimationFrame(applyTransform));

      render();
    });
  }

  // -------------------------------------------------------------------------
  // Media carousel - <lv1-carousel> custom element.
  // Vanilla, no swipe library. Behaviors:
  //   - prev/next + dots switch slides (translate3d on the track)
  //   - left/right arrow keys when focus is inside the carousel
  //   - touch swipe (threshold 40px or 15% of the viewport width)
  //   - aria-selected on dots + aria-hidden on inactive slides update on change
  //   - CLAMPED, never wrapping: at either end the matching arrow goes disabled
  //   - no auto-advance (product imagery should be user-driven)
  //
  // Every index change reflects onto `data-lv1-index` and fires a bubbling
  // `lv1-carousel:change` CustomEvent, so companions (see initReviewSync)
  // observe the carousel instead of reaching into its internals.
  // -------------------------------------------------------------------------

  const CAROUSEL_CHANGE = 'lv1-carousel:change';

  function defineCarousel() {
    if (typeof customElements === 'undefined') return;
    // Guarded: a second, unguarded define() would throw NotSupportedError and
    // take the rest of this IIFE down with it.
    if (customElements.get('lv1-carousel')) return;

    class Lv1Carousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-lv1-carousel-track]');
        this.slides = Array.from(this.querySelectorAll('.lv1-carousel__slide'));
        // Arrows are matched by data hook first, class second, so a leaf snippet
        // may emit either without the carousel losing its controls.
        this.prev = this.querySelector('[data-lv1-carousel-prev], .lv1-carousel__btn--prev');
        this.next = this.querySelector('[data-lv1-carousel-next], .lv1-carousel__btn--next');
        this.dots = Array.from(this.querySelectorAll('[data-lv1-carousel-dot]'));
        this.viewport = this.querySelector('.lv1-carousel__viewport') || this;
        if (!this.track || this.slides.length === 0) return;

        this.index = 0;
        this.count = this.slides.length;
        // Opt-in: only carousels that ask for it wrap around. S5's product
        // gallery deliberately stays clamped.
        this.loop = this.hasAttribute('data-lv1-carousel-loop');

        if (this.count <= 1) {
          this.update();
          return;
        }

        this.prev && this.prev.addEventListener('click', () => this.go(this.index - 1));
        this.next && this.next.addEventListener('click', () => this.go(this.index + 1));
        this.dots.forEach((d, i) => d.addEventListener('click', () => this.go(i)));

        this.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index - 1); }
          else if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.index + 1); }
        });

        // Touch swipe
        let startX = null, deltaX = 0;
        this.viewport.addEventListener('touchstart', (e) => {
          if (e.touches.length !== 1) return;
          startX = e.touches[0].clientX;
          deltaX = 0;
        }, { passive: true });
        this.viewport.addEventListener('touchmove', (e) => {
          if (startX === null) return;
          deltaX = e.touches[0].clientX - startX;
        }, { passive: true });
        this.viewport.addEventListener('touchend', () => {
          if (startX === null) return;
          const w = this.viewport.getBoundingClientRect().width || 1;
          const threshold = Math.max(40, w * 0.15);
          if (Math.abs(deltaX) > threshold) {
            this.go(this.index + (deltaX < 0 ? 1 : -1));
          }
          startX = null;
          deltaX = 0;
        });

        this.update();
      }

      go(n) {
        // Looping carousels wrap past either end; the rest clamp, and their end
        // arrow just stays disabled.
        const i = this.loop && this.count > 1
          ? ((n % this.count) + this.count) % this.count
          : Math.max(0, Math.min(this.count - 1, n));
        if (i === this.index) return;
        this.index = i;
        this.update();
      }

      update() {
        if (this.track) {
          this.track.style.transform = 'translate3d(' + (-this.index * 100) + '%, 0, 0)';
        }
        this.slides.forEach((s, i) => {
          s.toggleAttribute('aria-hidden', i !== this.index);
        });
        this.dots.forEach((d, i) => {
          const active = i === this.index;
          d.classList.toggle('is-active', active);
          d.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        if (this.prev) this.prev.toggleAttribute('disabled', !this.loop && this.index === 0);
        if (this.next) this.next.toggleAttribute('disabled', !this.loop && this.index === this.count - 1);

        this.setAttribute('data-lv1-index', String(this.index));
        this.dispatchEvent(new CustomEvent(CAROUSEL_CHANGE, {
          bubbles: true,
          detail: { index: this.index, count: this.count }
        }));
      }
    }

    customElements.define('lv1-carousel', Lv1Carousel);
  }

  // -------------------------------------------------------------------------
  // Review sync - pairs a media carousel with a separate quote pane.
  //
  // A <lv1-carousel data-lv1-review-sync="quotes-abc123"> tells us to keep
  // the container `#quotes-abc123` in step: the child whose
  // [data-lv1-review-index] matches the carousel's index gets `.is-active`
  // and aria-hidden="false"; every sibling loses both.
  //
  // The listener is delegated on document and driven purely by the public
  // `lv1-carousel:change` event, so it also picks up carousels upgraded after
  // this runs (and never touches the element's internals).
  // -------------------------------------------------------------------------

  function syncReviewPanes(carousel, index) {
    const id = carousel.getAttribute('data-lv1-review-sync');
    if (!id) return;
    const container =
      document.getElementById(id) ||
      document.querySelector('[data-lv1-review-sync-target="' + id + '"]');
    if (!container) return;
    const panes = container.querySelectorAll('[data-lv1-review-index]');
    if (panes.length === 0) return;
    panes.forEach((pane) => {
      const paneIndex = parseInt(pane.getAttribute('data-lv1-review-index'), 10);
      const isActive = paneIndex === index;
      pane.classList.toggle('is-active', isActive);
      pane.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });
  }

  function initReviewSync() {
    document.addEventListener(CAROUSEL_CHANGE, (e) => {
      const carousel = e.target;
      if (!carousel || typeof carousel.getAttribute !== 'function') return;
      if (!carousel.hasAttribute('data-lv1-review-sync')) return;
      const index = e.detail && typeof e.detail.index === 'number' ? e.detail.index : 0;
      syncReviewPanes(carousel, index);
    });

    // Carousels that upgraded during defineCarousel() already fired their first
    // change event before the listener above existed - catch those up once.
    document.querySelectorAll('[data-lv1-review-sync]').forEach((carousel) => {
      const index = parseInt(carousel.getAttribute('data-lv1-index'), 10);
      syncReviewPanes(carousel, isNaN(index) ? 0 : index);
    });
  }

  // -------------------------------------------------------------------------
  // Review step controls - the prev/next buttons under the quote stack.
  //
  // The media carousel is the single source of truth: the buttons drive it with
  // its public go(), and the change event it fires is what moves the quote pane
  // (via initReviewSync above). That keeps image and quote from ever drifting
  // apart. When a stack has no paired carousel, or the carousel holds a single
  // slide, the buttons fall back to stepping the panes directly.
  // -------------------------------------------------------------------------

  function reviewStackFor(btn) {
    const id = btn.getAttribute('aria-controls');
    const container = id ? document.getElementById(id) : btn.closest('[data-lv1-review]');
    if (!container) return null;
    const panes = Array.from(container.querySelectorAll('[data-lv1-review-index]'));
    if (panes.length === 0) return null;
    const carousel = id ? document.querySelector('[data-lv1-review-sync="' + id + '"]') : null;
    return { container, panes, carousel, id };
  }

  function currentReviewIndex(stack) {
    const active = stack.panes.findIndex((p) => p.classList.contains('is-active'));
    return active === -1 ? 0 : active;
  }

  function setReviewButtonState(stack) {
    if (!stack.id) return;
    const total = stack.panes.length;
    const i = currentReviewIndex(stack);
    // A looping stack never runs out of reviews, so its buttons never disable.
    const loop = !!(stack.carousel && stack.carousel.loop) || total > 1;
    document.querySelectorAll('[aria-controls="' + stack.id + '"]').forEach((b) => {
      if (b.hasAttribute('data-lv1-review-prev')) b.toggleAttribute('disabled', !loop && i === 0);
      if (b.hasAttribute('data-lv1-review-next')) b.toggleAttribute('disabled', !loop && i === total - 1);
    });
  }

  function stepReview(btn, delta) {
    const stack = reviewStackFor(btn);
    if (!stack) return;
    const total = stack.panes.length;
    const raw = currentReviewIndex(stack) + delta;
    // Reviews cycle: stepping past the last one returns to the first.
    const next = ((raw % total) + total) % total;

    // Prefer driving the carousel so the media follows.
    if (stack.carousel && typeof stack.carousel.go === 'function' && stack.carousel.count > 1) {
      stack.carousel.go(next);
    } else {
      stack.panes.forEach((pane, i) => {
        const isActive = i === next;
        pane.classList.toggle('is-active', isActive);
        pane.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });
    }
    setReviewButtonState(stack);
  }

  function initReviewControls() {
    const btns = document.querySelectorAll('[data-lv1-review-prev], [data-lv1-review-next]');
    if (btns.length === 0) return;
    btns.forEach((btn) => {
      btn.addEventListener('click', () => {
        stepReview(btn, btn.hasAttribute('data-lv1-review-prev') ? -1 : 1);
      });
    });
    // A carousel change (arrow, dot, swipe) also moves the stack, so keep the
    // button disabled state in step with it rather than only with our clicks.
    document.addEventListener(CAROUSEL_CHANGE, (e) => {
      const c = e.target;
      if (!c || typeof c.getAttribute !== 'function') return;
      const id = c.getAttribute('data-lv1-review-sync');
      if (!id) return;
      const anyBtn = document.querySelector('[aria-controls="' + id + '"]');
      if (anyBtn) setReviewButtonState(reviewStackFor(anyBtn) || { panes: [], id: null });
    });
    btns.forEach((btn) => {
      const stack = reviewStackFor(btn);
      if (stack) setReviewButtonState(stack);
    });
  }

  // -------------------------------------------------------------------------
  // Email capture.
  //
  // Two jobs: stamp the scanned SKU onto the tag list, and submit without a
  // reload so the page does not shift.
  //
  // Submitting has one hard constraint. This store has Shopify spam protection
  // on customer forms, and a background fetch to /contact is answered with
  // 400 "Missing CAPTCHA token": Shopify injects that token from its own
  // handler during a NATIVE submit, which fetch never triggers. So the submit
  // has to stay native. It is simply retargeted into a hidden same-origin
  // iframe, which keeps Shopify's handler in play while the top-level document
  // stays put.
  //
  // Note this only works because the browser fires a real submit event. Calling
  // form.submit() directly bypasses the event, Shopify's handler never runs, and
  // the post fails the same way fetch does.
  // -------------------------------------------------------------------------

  // Shopify splits tags on commas and caps them at 255 chars.
  function cleanTagPart(v) {
    return String(v).replace(/,/g, ' ').trim().replace(/\s+/g, '-').slice(0, 64);
  }

  // A scanned code arrives as ?sku=XYZ. Record which SKU sent them so the
  // audience can be sliced per product, not just per campaign.
  function applySkuTag(capture) {
    const campaign = capture.getAttribute('data-lv1-capture-campaign');
    const input = capture.querySelector('[data-lv1-capture-tags]');
    if (!campaign || !input) return;
    let sku;
    try { sku = new URLSearchParams(window.location.search).get('sku'); } catch (e) { return; }
    if (!sku) return;
    const clean = cleanTagPart(sku);
    if (!clean) return;
    const tag = campaign + '-sku-' + clean;
    const have = input.value.split(',').map((t) => t.trim());
    if (have.indexOf(tag) === -1) input.value = input.value + ',' + tag;
  }

  function initCapture() {
    document.querySelectorAll('[data-lv1-capture]').forEach((capture, idx) => {
      applySkuTag(capture);

      const form = capture.querySelector('.lv1-capture__form');
      if (!form) return;
      const body = form.querySelector('[data-lv1-capture-body]');
      const thanks = form.querySelector('[data-lv1-capture-thanks]');
      const error = form.querySelector('[data-lv1-capture-error]');
      const input = form.querySelector('input[name="contact[email]"]');
      const submit = form.querySelector('.lv1-capture__submit');
      const spinner = form.querySelector('.lv1-capture__spinner');
      const label = form.querySelector('.lv1-capture__submit-label');
      if (!body || !thanks || !input || !submit) return;

      const showError = (msg) => {
        if (!error) return;
        error.textContent = msg;
        error.hidden = false;
        input.setAttribute('aria-invalid', 'true');
        input.setAttribute('aria-describedby', error.id);
      };
      const clearError = () => {
        if (!error) return;
        error.hidden = true;
        error.textContent = '';
        input.removeAttribute('aria-invalid');
        input.removeAttribute('aria-describedby');
      };
      const setLoading = (on) => {
        submit.toggleAttribute('disabled', on);
        submit.setAttribute('aria-busy', on ? 'true' : 'false');
        spinner && spinner.classList.toggle('hidden', !on);
        // visibility, not display: the label must keep its space or the button
        // collapses mid-request.
        label && label.classList.toggle('is-hidden', on);
      };
      input.addEventListener('input', clearError);

      const sinkName = 'lv1-capture-sink-' + idx;
      const sink = document.createElement('iframe');
      sink.name = sinkName;
      sink.title = 'Signup handler';
      sink.setAttribute('aria-hidden', 'true');
      sink.setAttribute('tabindex', '-1');
      sink.style.cssText = 'position:absolute;width:0;height:0;border:0;left:-9999px;';
      document.body.appendChild(sink);
      form.setAttribute('target', sinkName);

      let pending = false;
      let frozenHeight = 0;
      let watchdog = null;

      // Shopify's spam protection is adaptive: a submission it judges suspicious
      // is answered with "Missing CAPTCHA token" rather than processed, and its
      // handler can swallow the submit before it ever reaches the iframe. If
      // nothing comes back within 3s, stop waiting and re-send the form as a
      // normal top-level navigation. That costs the reload this code exists to
      // avoid, but it is the difference between a slower signup and a lost one.
      const escalate = () => {
        if (!pending) return;
        pending = false;
        setLoading(false);
        form.removeAttribute('target');
        form.submit();
      };

      sink.addEventListener('load', () => {
        if (!pending) return;               // the initial about:blank load
        pending = false;
        clearTimeout(watchdog);
        setLoading(false);

        // Same-origin, so the response can be inspected. Only a positively
        // identified failure blocks the success state; an unreadable response
        // still means the native post went through.
        let failure = null;
        try {
          const doc = sink.contentDocument;
          const text = doc && doc.body ? doc.body.innerText : '';
          if (/Missing CAPTCHA token|connection needs to be verified/i.test(text)) {
            failure = 'Please try again.';
          } else {
            const el = doc && doc.querySelector('.form__message--error, .newsletter-form__message--error');
            if (el) failure = el.textContent.trim();
          }
        } catch (e) { /* unreadable: treat the native post as delivered */ }

        if (failure) { showError(failure); return; }

        if (frozenHeight) thanks.style.minHeight = frozenHeight + 'px';
        body.hidden = true;
        thanks.hidden = false;
        thanks.focus();
      });

      form.addEventListener('submit', (e) => {
        clearError();
        if (!input.checkValidity()) {
          e.preventDefault();
          showError(input.validationMessage || 'Enter a valid email address.');
          input.focus();
          return;
        }
        // Deliberately NOT prevented: the native submit is what lets Shopify
        // attach its CAPTCHA token. It lands in the iframe, not this document.
        frozenHeight = Math.round(body.getBoundingClientRect().height);
        pending = true;
        setLoading(true);
        clearTimeout(watchdog);
        watchdog = setTimeout(escalate, 3000);
      });
    });
  }

  function init() {
    initVideoSliders();
    defineCarousel();
    initReviewSync();
    initReviewControls();
    initCapture();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
